import React from "react";
import PropTypes from "prop-types";
import { Steps, Icon, Badge } from "antd";
import { List } from "immutable";

/**
 * Stage tab strip for the Inbox / Participated views.
 *
 * Shows ONLY the user's own stages (the ones flagged with `!key` in the
 * stage record — same predicate `getDefaultTab` uses to pick a landing
 * tab). There is no expand-to-all toggle: for most credit team users the
 * user's own stages fit on one row and cross-team lookups are not the
 * main use-case here.
 *
 * When a `caseCounts` immutable Map (keyed by `stage_name`) is passed in,
 * each stage's tab shows an antd Badge with the count so credit/ops can
 * see their queue depth without opening a bucket.
 */
class ExpandableSteps extends React.Component {
    filteredOptions = List([]);

    componentDidMount() {
        this.applyFilter();
    }

    componentDidUpdate(prevProps) {
        // Re-run the filter when the options list itself changes (fresh fetch).
        if (this.props.options.size !== prevProps.options.size) {
            this.applyFilter();
        }
    }

    applyFilter = () => {
        // Keep only stages the user actually works on (the flag is false / null
        // on those). Everything else is a third-party stage the toggle used to
        // reveal — no longer wanted.
        this.filteredOptions = this.props.options.filter(_ => !_.key);
        this.forceUpdate();
    };

    onChange = activeIndex => {
        const stage = this.filteredOptions.find(
            (_, index) => index === activeIndex
        );
        this.props.onTabChange(stage);
    };

    getStageCount(stageName) {
        const { caseCounts } = this.props;
        if (!caseCounts || !caseCounts.get) {
            return null;
        }
        const n = caseCounts.get(stageName);
        return typeof n === "number" ? n : null;
    }

    render() {
        const { currentStage } = this.props;
        const currentStageIndex = this.filteredOptions.findIndex(
            _ => _.stage_name === currentStage.stage_name
        );
        return (
            <div className={"stage-tabs-wrap"}>
                <Steps
                    className={"stage-tabs"}
                    type={"navigation"}
                    size={"small"}
                    current={currentStageIndex}
                    onChange={this.onChange}
                    style={this.props.style}
                >
                    {this.filteredOptions
                        .map(_ => {
                            const count = this.getStageCount(_.stage_name);
                            return (
                                <Steps.Step
                                    status={null}
                                    key={_.id}
                                    description={
                                        !_.key ? (
                                            <i>{"You work here"}</i>
                                        ) : null
                                    }
                                    title={
                                        <div className={"stage-tabs__title"}>
                                            <span
                                                className={
                                                    "stage-tabs__name"
                                                }
                                            >
                                                {_ && _.stage_name}
                                            </span>
                                            {count !== null && count > 0 ? (
                                                <Badge
                                                    count={count}
                                                    overflowCount={999}
                                                    style={{
                                                        backgroundColor:
                                                            "#2c5fe0"
                                                    }}
                                                />
                                            ) : null}
                                            {this.props.toggleHelp ? (
                                                <Icon type="info-circle" />
                                            ) : null}
                                        </div>
                                    }
                                />
                            );
                        })
                        .toArray()}
                </Steps>
            </div>
        );
    }
}

ExpandableSteps.propTypes = {
    onTabChange: PropTypes.func,
    currentStage: PropTypes.oneOfType([PropTypes.object, PropTypes.number]),
    style: PropTypes.object,
    toggleHelp: PropTypes.bool,
    options: PropTypes.object,
    // Immutable Map keyed by `stage_name`. Optional — if absent, count
    // badges are hidden.
    caseCounts: PropTypes.object
};

export default ExpandableSteps;
